import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Building, TrendingUp, Users } from 'lucide-react';
import { useComponentTranslations } from '../hooks/useTranslations';

const Affiliates = () => {
  const { t, loading } = useComponentTranslations('affiliates');

  if (loading) {
    return (
      <section id="affiliates" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse mb-4"></div>
            <div className="h-6 bg-gray-200 rounded-lg animate-pulse max-w-2xl mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-8">
                <div className="h-32 bg-gray-200 rounded animate-pulse mb-6"></div>
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4"></div>
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const companies = [
    {
      name: 'Dreamline Operations LLC',
      description: t('operations_desc', 'Strategic operational excellence and business process optimization for sustainable growth and efficiency.'),
      url: 'https://dreamline.world/',
      icon: Building,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Dreamline Services LLC',
      description: t('services_desc', 'Comprehensive business services and consulting solutions tailored to meet diverse client needs and objectives.'),
      url: 'https://dreamline.services/lander',
      icon: TrendingUp,
      color: 'from-teal-500 to-green-500'
    },
    {
      name: 'Harmony Living By V',
      description: t('harmony_desc', 'Innovative lifestyle and wellness solutions focused on creating harmonious living experiences for modern families.'),
      url: 'https://harmonylivingbyv.com/',
      icon: Users,
      color: 'from-purple-500 to-pink-500'
    }
  ];

  const handleLearnMore = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="affiliates" className="py-20 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
          >
            {t('title', 'Our Portfolio Companies')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('subtitle', 'Driving excellence across diverse business ventures through strategic governance and operational support')}
          </motion.p>
        </div>

        {/* Companies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {companies.map((company, index) => (
            <motion.div
              key={company.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${company.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <company.icon className="w-8 h-8 text-white" />
              </div>

              {/* Company Name */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                {company.name}
              </h3>

              {/* Description */}
              <p className="text-gray-600 mb-6 leading-relaxed">
                {company.description}
              </p>

              {/* Learn More Button */}
              <button
                onClick={() => handleLearnMore(company.url)}
                className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-300 group-hover:translate-x-2"
              >
                {t('learn_more', 'Learn More')}
                <ExternalLink className="ml-2 w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-8 md:p-12"
        >
          <div className="text-center text-white">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              {t('portfolio_strength', 'Portfolio Strength')}
            </h3>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              {t('portfolio_desc', 'Our diversified portfolio represents excellence across multiple industries and markets')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">3+</div>
                <div className="text-blue-100">{t('active_companies', 'Active Companies')}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">5+</div>
                <div className="text-blue-100">{t('industries', 'Industries')}</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">10+</div>
                <div className="text-blue-100">{t('years_growth', 'Years of Growth')}</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Partnership CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-4">
            {t('partnership_title', 'Interested in Partnership Opportunities?')}
          </h3>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('partnership_desc', 'Explore how Dreamline Global can provide strategic value to your business ventures')}
          </p>
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {t('contact_us', 'Contact Us Today')}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Affiliates;